@if (!hasAnyTiers()) {
    <div class="late-fee-timeline late-fee-timeline--empty">
        <fa-duotone-icon icon="chart-simple" secondaryOpacity="0.9" class="me-2" />
        <span>Add a tier to see how late fees will accrue.</span>
    </div>
} @else {
    <div class="late-fee-timeline">

        @if (dueDateObj()) {
            <div class="late-fee-timeline__anchor">
                <span class="late-fee-timeline__anchor-swatch" aria-hidden="true"></span>
                Due date: <strong>{{ formatDate(dueDateObj()) }}</strong>
            </div>
        } @else {
            <div class="late-fee-timeline__anchor late-fee-timeline__anchor--muted">
                <fa-duotone-icon icon="circle-info" secondaryOpacity="0.9" class="me-1" />
                Set a due date above to see exact calendar dates.
            </div>
        }

        <svg class="late-fee-timeline__defs" aria-hidden="true" focusable="false">
            <defs>
                <linearGradient id="late-fee-fade" x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0%" stop-color="currentColor" stop-opacity="0.75" />
                    <stop offset="100%" stop-color="currentColor" stop-opacity="0" />
                </linearGradient>

                <pattern id="late-fee-grace-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                    <rect width="8" height="8" class="late-fee-timeline__grace-bg" />
                    <line x1="0" y1="0" x2="0" y2="8" class="late-fee-timeline__grace-stripe" />
                </pattern>

            </defs>
        </svg>

        <div class="late-fee-timeline__rows">
            @if (hoveredScaleDay() !== null) {
                <div class="late-fee-timeline__scrub-line-overlay"
                     [style.--scrub-fraction]="scrubLeftFraction()"></div>
            }

            <div class="late-fee-timeline__row late-fee-timeline__row--scale">
                <div class="late-fee-timeline__row-label late-fee-timeline__row-label--scale">Day</div>
                <div class="late-fee-timeline__row-plot late-fee-timeline__row-plot--scale"
                     (mouseenter)="onScaleMouseMove($event)"
                     (mousemove)="onScaleMouseMove($event)"
                     (mouseleave)="onScaleMouseLeave()">
                    <svg class="late-fee-timeline__scale-svg"
                         [attr.viewBox]="'0 0 ' + viewBoxWidth + ' 24'"
                         preserveAspectRatio="none"
                         role="img"
                         aria-label="Day scale (days after due date)">
                        <line class="late-fee-timeline__scale-baseline"
                              [attr.x1]="0" [attr.x2]="viewBoxWidth"
                              [attr.y1]="22" [attr.y2]="22" />
                        @for (day of dayTicks(); track day) {
                            <line [class]="'late-fee-timeline__scale-tick' + (isMajorTick(day) ? ' late-fee-timeline__scale-tick--major' : '')"
                                  [attr.x1]="dayToX(day)" [attr.x2]="dayToX(day)"
                                  [attr.y1]="isMajorTick(day) ? 10 : 16"
                                  [attr.y2]="22" />
                        }
                    </svg>

                    @if (hoveredScaleDay(); as scrubDay) {
                        @let cumulative = scrubCumulativeFees(scrubDay);
                        @let scrubDateValue = scrubDate(scrubDay);
                        <div class="late-fee-timeline__tooltip late-fee-timeline__tooltip--scrub"
                             [class.late-fee-timeline__tooltip--right]="scrubLeftPercent() < 25"
                             [class.late-fee-timeline__tooltip--left]="scrubLeftPercent() > 75"
                             [style.left.%]="scrubLeftPercent()">
                            <div class="late-fee-timeline__tooltip-date">
                                @if (scrubDateValue) {
                                    {{ formatDate(scrubDateValue) }}
                                } @else {
                                    Day {{ scrubDay }} after due
                                }
                            </div>
                            @if (cumulative.totalFees > 0) {
                                @for (tier of cumulative.perTier; track tier.tierNumber) {
                                    <div class="late-fee-timeline__tooltip-row">
                                        <span [class]="'late-fee-timeline__tooltip-swatch late-fee-timeline__swatch--color-' + tier.colorIndex"
                                              aria-hidden="true"></span>
                                        <span class="late-fee-timeline__tooltip-label">Tier {{ tier.tierNumber }}:</span>
                                        <strong>{{ formatCurrency(tier.fees) }}</strong>
                                        <span class="late-fee-timeline__tooltip-muted">({{ tier.count }} fee{{ tier.count === 1 ? '' : 's' }})</span>
                                    </div>
                                }
                                <div class="late-fee-timeline__tooltip-row late-fee-timeline__tooltip-row--total">
                                    <span class="late-fee-timeline__tooltip-label">Total so far:</span>
                                    <strong>{{ formatCurrency(cumulative.totalFees) }}</strong>
                                </div>
                            } @else {
                                <div class="late-fee-timeline__tooltip-row">
                                    <span class="late-fee-timeline__tooltip-muted">No late fees by this day</span>
                                </div>
                            }
                        </div>
                    }
                </div>
                <div class="late-fee-timeline__row-spacer"></div>
            </div>

            @for (row of tierRows(); track trackRow($index, row)) {
                <div class="late-fee-timeline__row">
                    <div class="late-fee-timeline__row-label">
                        <div class="late-fee-timeline__row-label-title">Tier {{ row.rowIndex + 1 }}</div>
                        <div class="late-fee-timeline__row-label-amount">{{ formatCurrency(row.amount) }}</div>
                    </div>

                    <div class="late-fee-timeline__row-plot">
                        <svg [class]="'late-fee-timeline__svg late-fee-timeline__segment--color-' + row.colorIndex"
                             [attr.viewBox]="'0 0 ' + viewBoxWidth + ' ' + viewBoxHeight"
                             preserveAspectRatio="none"
                             role="img"
                             [attr.aria-label]="'Tier ' + (row.rowIndex + 1) + ' late fee accrual timeline'">

                            <rect class="late-fee-timeline__due-bar"
                                  [attr.x]="0"
                                  [attr.y]="bandTop"
                                  [attr.width]="dayToX(1)"
                                  [attr.height]="bandHeight"
                                  [attr.rx]="2" [attr.ry]="2"
                                  [attr.aria-label]="dueDateObj() ? 'Due date: ' + formatDate(dueDateObj()) : 'Due date'"
                                  (mouseenter)="onDueBarEnter(row.rowIndex)"
                                  (mouseleave)="onDueBarLeave()" />

                            @if (row.gracePeriodDays > 0) {
                                <rect class="late-fee-timeline__band late-fee-timeline__band--grace"
                                      [attr.x]="graceX(row)"
                                      [attr.y]="bandTop"
                                      [attr.width]="graceWidth(row)"
                                      [attr.height]="bandHeight"
                                      [attr.rx]="2" [attr.ry]="2" />
                            }

                            @if (row.occurrenceType !== 'Once') {
                                <rect class="late-fee-timeline__band late-fee-timeline__band--tier"
                                      [attr.x]="accrualBandX(row)"
                                      [attr.y]="bandTop"
                                      [attr.width]="accrualBandWidth(row)"
                                      [attr.height]="bandHeight"
                                      [attr.rx]="2" [attr.ry]="2" />

                                @if (row.indefinite) {
                                    <rect class="late-fee-timeline__band-fade"
                                          [attr.x]="accrualBandX(row)"
                                          [attr.y]="bandTop"
                                          [attr.width]="accrualBandWidth(row)"
                                          [attr.height]="bandHeight" />
                                }
                            }
                        </svg>

                        <div [class]="'late-fee-timeline__markers late-fee-timeline__segment--color-' + row.colorIndex">
                            @for (marker of markers(); track trackMarker($index, marker)) {
                                @if (marker.rowIndex === row.rowIndex) {
                                    <button type="button"
                                            [class]="'late-fee-timeline__marker' + (isMarkerActive(marker) ? ' late-fee-timeline__marker--active' : '')"
                                            [style.left.%]="markerXPercent(marker)"
                                            (mouseenter)="onMarkerEnter(marker)"
                                            (mouseleave)="onMarkerLeave()"
                                            (focus)="onMarkerEnter(marker)"
                                            (blur)="onMarkerLeave()"
                                            [attr.aria-label]="'Late fee accrual ' + marker.accrualNumber + ' of tier ' + marker.tierNumber">
                                        <span class="late-fee-timeline__marker-dot"></span>
                                    </button>
                                }
                            }
                        </div>

                        @if (isDueBarHovered(row.rowIndex)) {
                            <div class="late-fee-timeline__tooltip late-fee-timeline__tooltip--right"
                                 [class.late-fee-timeline__tooltip--multiline]="!dueDateObj()"
                                 [style.left.%]="dueBarTooltipXPercent()">
                                <div class="late-fee-timeline__tooltip-date">
                                    @if (dueDateObj()) {
                                        {{ formatDate(dueDateObj()) }}
                                    } @else {
                                        Due date
                                    }
                                </div>
                                @if (dueDateObj()) {
                                    <div class="late-fee-timeline__tooltip-row">
                                        <span class="late-fee-timeline__tooltip-label">Due date</span>
                                    </div>
                                } @else {
                                    <div class="late-fee-timeline__tooltip-row">
                                        <span class="late-fee-timeline__tooltip-muted">Select a due date above to see exact calendar dates.</span>
                                    </div>
                                }
                            </div>
                        }

                        @if (hoveredTooltipForRow(row.rowIndex); as marker) {
                            <div class="late-fee-timeline__tooltip"
                                 [class.late-fee-timeline__tooltip--right]="tooltipXPercent(marker) < 25"
                                 [class.late-fee-timeline__tooltip--left]="tooltipXPercent(marker) > 75"
                                 [style.left.%]="tooltipXPercent(marker)">
                                <div class="late-fee-timeline__tooltip-date">
                                    @if (marker.date) {
                                        {{ formatDate(marker.date) }}
                                    } @else {
                                        Day {{ marker.day }} after due
                                    }
                                </div>
                                <div class="late-fee-timeline__tooltip-row">
                                    <span class="late-fee-timeline__tooltip-label">Late fee:</span>
                                    <strong>{{ formatCurrency(marker.amount) }}</strong>
                                </div>
                                <div class="late-fee-timeline__tooltip-row">
                                    <span class="late-fee-timeline__tooltip-label">Day:</span>
                                    {{ marker.day }} after due
                                </div>
                                <div class="late-fee-timeline__tooltip-row">
                                    <span class="late-fee-timeline__tooltip-label">Tier:</span>
                                    {{ marker.tierNumber }}
                                    @if (marker.accrualsInTier !== undefined) {
                                        <span class="late-fee-timeline__tooltip-muted">
                                            (accrual {{ marker.accrualNumber }} of {{ marker.accrualsInTier }})
                                        </span>
                                    } @else {
                                        <span class="late-fee-timeline__tooltip-muted">
                                            (accrual {{ marker.accrualNumber }})
                                        </span>
                                    }
                                </div>
                                <div class="late-fee-timeline__tooltip-row late-fee-timeline__tooltip-row--total">
                                    <span class="late-fee-timeline__tooltip-label">Tier total so far:</span>
                                    <strong>{{ formatCurrency(marker.cumulativeTotal) }}</strong>
                                    @if (marker.indefiniteTier) {
                                        <span class="late-fee-timeline__tooltip-muted">accrues until paid</span>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                    <div class="late-fee-timeline__row-spacer"></div>
                </div>
            }
        </div>

        <div class="late-fee-timeline__captions">
            @for (row of tierRows(); track trackRow($index, row)) {
                <div class="late-fee-timeline__caption">
                    <div [class]="'late-fee-timeline__caption-swatch late-fee-timeline__swatch--color-' + row.colorIndex"></div>
                    <div class="late-fee-timeline__caption-body">
                        <div class="late-fee-timeline__caption-title">
                            Tier {{ row.rowIndex + 1 }}
                            @if (row.indefinite) {
                                <span class="late-fee-timeline__badge">continues until paid</span>
                            }
                        </div>
                        <div class="late-fee-timeline__caption-detail">
                            {{ formatCurrency(row.amount) }} ·
                            {{ row.gracePeriodDays }}-day grace ·
                            {{ occurrenceLabel(row) }}
                            @if (!row.indefinite && row.accrualCount !== undefined) {
                                · total <strong>{{ formatCurrency(row.tierTotal) }}</strong>
                            }
                        </div>
                        @if (row.startDate) {
                            <div class="late-fee-timeline__caption-dates">
                                first fee <strong>{{ formatDate(row.startDate) }}</strong>
                                @if (row.endDate) {
                                    · last fee <strong>{{ formatDate(row.endDate) }}</strong>
                                } @else if (row.indefinite) {
                                    · continues until invoice is paid
                                }
                            </div>
                        }
                    </div>
                </div>
            }
        </div>

        <div class="late-fee-timeline__summary">
            @if (hasIndefiniteTier()) {
                <span>
                    Definite fees total
                    <strong>{{ formatCurrency(definiteTotal()) }}</strong>;
                    one or more tiers continue until the invoice is paid.
                </span>
            } @else if (definiteTotal() > 0) {
                <span>
                    Max late fees across all tiers:
                    <strong>{{ formatCurrency(definiteTotal()) }}</strong>
                </span>
            }
        </div>
    </div>
}
